/* eslint-disable react-refresh/only-export-components */
import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-0 disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline:
          "border border-input bg-background hover:bg-accent hover:text-accent-foreground",
        outlineAlt:
          "border border-slate-900 bg-transparent hover:bg-foreground hover:text-background",
        outlineInverted:
          "border border-sidebar-foregroundSelected bg-transparent hover:bg-sidebar-backgroundSelected hover:text-sidebar-foregroundSelected",
        outlineInvertedNoHover:
          "border border-sidebar-foregroundSelected bg-transparent",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        ghostish:
          "hover:bg-accent hover:text-accent-foreground bg-secondary/50",
        link: "text-primary underline-offset-4 hover:underline",
        sidebarSelected:
          "bg-sidebar-backgroundSelected text-sidebar-foregroundSelected border-l-4 border-l-sidebar-foregroundSelected",
        sidebar:
          "border-l-4 border-l-transparent bg-sidebar-background text-sidebar-foreground hover:bg-sidebar-backgroundSelected/50 hover:text-sidebar-foregroundSelected hover:border-l-sidebar-foregroundSelected/50 hover:border-l-4",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
